/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { Heading, Button, Text, Icon, CardCarousel } from 'sarsaparilla';
import { useUserLocation } from 'shared-ui/geolocation';

import {
    useTicketsAvailableThisWeekendQuery,
    RECOMMENDATION_RADIUS,
} from '../api/search-api.queries';
import { CarouselCard } from '../CarouselCard/CarouselCard';
import { dateToSillySearchFormat } from '../utils';
import { getNextWeekend } from '../utils/getNextWeekend';
import { HeroCarouselSkeleton } from './HeroCarouselSkeleton';
import { CarouselError } from './CarouselError';

export function TicketsCarousel() {
    const userLocation = useUserLocation();

    const { data, isError, isPending } = useTicketsAvailableThisWeekendQuery({
        isEnabled: userLocation !== null,
        latitude: userLocation?.latitude,
        longitude: userLocation?.longitude,
        location: userLocation?.location,
    });

    const cards = React.useMemo(() => {
        if (!data?.results) {
            return [];
        }

        return data?.results?.map((item) => {
            return (
                <CarouselCard
                    key={item.id}
                    item={item}
                    showAvailDates
                    onClick={() => {}}
                    gaTrackingId="899974145790"
                    gaTrackingValue={item.name}
                    showRating={false}
                />
            );
        });
    }, [data?.results]);

    const exploreMoreHref = React.useMemo(() => {
        if (!userLocation) {
            return undefined;
        }

        return getExploreMoreHref(userLocation.latitude, userLocation.longitude);
    }, [userLocation]);

    return (
        <section>
            <Heading headingLevel={2} appearance="h6">
                Available This Weekend
            </Heading>
            <Text className="mb-2">
                Tickets and tours with upcoming availability.{' '}
                <Button
                    appearance="link"
                    href={exploreMoreHref}
                    isDisabled={!exploreMoreHref}
                    iconAfterElement={<Icon iconName="arrow-forward" />}
                    className="ml-half"
                    style={{ verticalAlign: 'baseline' }}
                    aria-label="Explore more nearby available tickets and tours"
                    gaTrackingId="899974145790"
                >
                    Explore More
                </Button>
            </Text>

            {isPending ? (
                <HeroCarouselSkeleton />
            ) : isError || !data?.results ? (
                <CarouselError />
            ) : (
                <CardCarousel
                    accessibleName="Available Tickets Nearby"
                    gaTrackingId="899974145790"
                    itemsPerBreakpoint={{ md: 4, xl: 5 }}
                    overscanPerBreakpoint={{ md: 12 }}
                >
                    {cards}
                </CardCarousel>
            )}
        </section>
    );
}

function getExploreMoreHref(latitude: number, longitude: number): string {
    const [startDate, endDate] = getNextWeekend();

    const searchParams = new URLSearchParams({
        inventory_type: 'tickets & tours',
        checkin: dateToSillySearchFormat(startDate),
        checkout: dateToSillySearchFormat(endDate),
        sort: 'available',
        lat: latitude.toString(),
        lng: longitude.toString(),
        clear: 'true',
        radius: RECOMMENDATION_RADIUS,
    });

    return `/search?${searchParams.toString()}`;
}

// cSpell:ignore checkin
